import { useEffect, useCallback } from "react";
import {
  Layout,
  Page,
  Card,
  Text,
  BlockStack,
  TextContainer,
  Button,
  InlineStack,
  Link,
  Spinner,
  Badge,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import PrettyDate from "components/format/PrettyDate";
import { fetchFileDetails, setDestroy } from "containers/file/file.Slice";
import { RootState, AppDispatch } from "configureStore";
import { useSelector, useDispatch } from "react-redux";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { openPdfPreview } from "components/modalx/modalx-openers";
import { History } from "lucide-react";
import { API_URL } from "../../constants";
import Loading from "components/loading/Loading";
import DocumentAnnulmentPanel from "components/document-annulment/DocumentAnnulmentPanel";
import { hasPdfExtension } from "utils/helperFunctions";
import { formatFileNumberWithSubNumber } from "utils/formatFileNumberDisplay";
import {
  getContactSignersArray,
  renderContactSignerStatuses,
} from "utils/contactSignerTableUtils";
import {
  openDokobitSignUrl,
  createHandleRepeatEmail,
} from "utils/signatureViewUtils";
import type { UserSigner } from "types/DokobitMeta";
import useIsPhone from "features/files/use-is-phone";
import SignerList, { SignerRowData, signerStatusOf } from "./signer-list";

function FileContainer({}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, fileId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const isPhone = useIsPhone();

  const loadFileData = useCallback(() => {
    if (fileId && companyId) {
      dispatch(fetchFileDetails({ fileId, companyId }));
    }
  }, [fileId, companyId, dispatch]);

  const { fileEntity, isLoading } = useSelector(
    (state: RootState) => state.fileContainerReducer
  );

  const categoryId = fileEntity?.categories?.length
    ? fileEntity?.categories[0]?.id
    : null;
  const fileViewUrl =
    fileEntity && fileEntity?.id && categoryId
      ? `/app/${companyId}/documents/${categoryId}/${fileEntity?.id}`
      : null;

  const { role } = useSelector((state: RootState) => state.authReducer);
  const isUserMaster = role?.permissionsgroups?.includes("Master");
  const hasUserDocsPermission =
    role?.permissionsgroups?.includes("Access document");

  useEffect(() => {
    loadFileData();

    return () => {
      dispatch(setDestroy());
    };
  }, [fileId, companyId, loadFileData]);

  const handleOnFileChange = () => {};

  // check strictly route param and fetch fileId
  const fetchedFileId = fileEntity?.id ?? null;
  if (fetchedFileId !== fileId) {
    return <Loading />;
  }

  if (!fileEntity) {
    return <Loading />;
  }

  const customFieldsContent = fileEntity.customFields
    ?.filter(({ value }) => value)
    .map(({ label, value, fieldType }) => {
      // Dates are always strings, only file fields can be arrays
      if (fieldType === "date") {
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong>
            <PrettyDate dateString={value as string} />
          </Text>
        );
      } else {
        // For non-date fields, handle arrays (like file fields)
        const displayValue = Array.isArray(value) 
          ? value.join(", ")
          : (value as string);
        return (
          <Text as="p" variant="bodyMd" key={label}>
            <strong>{t(label)} : </strong> {displayValue}
          </Text>
        );
      }
    });

  const categoriesNode = fileEntity?.categories
    ?.map((categoryEntity) => {
      return categoryEntity.title;
    })
    .join(" ,");

  const customersNode = fileEntity?.customer
    ?.map((customerEntity) => {
      return customerEntity.name;
    })
    .join(" ,");

  const handlePdfView = (id: string) => openPdfPreview(dispatch, id, companyId);

  const isSigned = fileEntity.isSigned;

  const isPdf = hasPdfExtension(fileEntity?.title || "");

  const dokobitSigners = fileEntity?.dokobit?.signersList ?? [];
  const dokobitContactSigners = getContactSignersArray(fileEntity?.dokobit?.contactSigners);
  const signontabSigners = fileEntity?.signontab?.signersList ?? [];
  const signontabContactSigners = getContactSignersArray(fileEntity?.signontab?.contactSigners);

  const handleReviewClick = ({
    id,
    fileId,
    disabled,
  }: {
    id: string;
    fileId: string;
    disabled: boolean;
  }) => {
    if (id && !disabled) {
      dispatch(setModalxComponent("FileReviewConfirmPopup"));
      dispatch(setModalxComponentProps({ id, fileId }));
    }
  };

  const handleClickSign = (fileId: string) => {
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(setModalxComponentProps({ fileId }));
  };

  const handleDeclineSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineClickSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleDeclineDokobitSignRequest = async (id: string) => {
    dispatch(setModalxComponent("DeclineDokobitSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
      })
    );
  };

  const handleDokobitSign = (fileId: string) =>
    openDokobitSignUrl(companyId!, fileId);

  const handleRepeatEmail = createHandleRepeatEmail(companyId!, () => {
    if (fileId) dispatch(fetchFileDetails({ fileId, companyId: companyId! }));
  });

  const handleDownloadFileVersion = (fileId: string, signatureId: string) => {
    // Open the file version download in a new tab
    window.open(
      `${API_URL}/${companyId}/files/download/${fileId}/signature/${signatureId}`,
      "_blank"
    );
  };

  // "Reminder history": opens the reminder modal, whose second section lists
  // every reminder ever sent for this document.
  //
  // This is the route every document link in the app opens
  // (`documents/view/:fileId`), including the "unsigned assigned documents"
  // widget's own row click — and that widget drops a document as soon as
  // nothing is awaited, so cancelling the signing used to hide the send
  // history for good (Desk #136). Hence no gating on signType, isSigned or the
  // presence of Signatures rows: the history outlives all three.
  //
  // `historyOnly` hides the editable settings section: this route sits outside
  // every ProtectedRoute, and the backend reminder-settings endpoint checks
  // company ownership only.
  const handleSigningHistoryClick = () => {
    dispatch(setModalxComponent("ReminderSettingsModal"));
    dispatch(setModalxStyle("large"));
    dispatch(
      setModalxComponentProps({
        fileId: fileEntity.id,
        companyId,
        historyOnly: true,
      })
    );
  };

  const dokobitRows: SignerRowData[] = [
    ...dokobitSigners.map((signer: any): SignerRowData => ({
      name: signer.name,
      date: signer.signed ? signer.signing_time : fileEntity?.dokobit?.createdAt || "",
      status: signer.signed ? "signed" : signerStatusOf(signer.status),
      declineReason: signer.declineReason,
      actions:
        !signer.signed && signer.status !== "rejected" && !fileEntity.isSigned && signer.id === role?.user ? (
          <>
            <Button size="slim" onClick={() => handleDokobitSign(fileEntity.id)}>
              {t("sign")}
            </Button>
            <Button size="slim" onClick={() => handleDeclineDokobitSignRequest(fileEntity.id)}>
              {t("decline")}
            </Button>
          </>
        ) : undefined,
    })),
    ...dokobitContactSigners.map((signer: any): SignerRowData => ({
      name: signer.name,
      date: signer.signed ? signer.signing_time : fileEntity?.dokobit?.createdAt || "",
      status: signer.signed ? "signed" : "pending",
      actions:
        signer.signatureId && !signer.signed && !fileEntity.isSigned ? (
          <Button size="slim" onClick={() => handleRepeatEmail(signer.signatureId)}>
            {t("repeat_email")}
          </Button>
        ) : undefined,
    })),
  ];

  const signontabRows: SignerRowData[] = [...signontabSigners, ...signontabContactSigners].map(
    (signer: any): SignerRowData => ({
      name: signer.name,
      date: signer.signed ? signer.signing_time : fileEntity?.signontab?.createdAt || "",
      status: signer.signed ? "signed" : "pending",
    })
  );

  const clickRows: SignerRowData[] = (fileEntity?.signersList ?? []).map((signer: any): SignerRowData => {
    const canAct = signer.status === "pending" && signer?.user?.id === role?.user;
    return {
      name: signer?.user?.name || signer?.contact?.name || "",
      date: signer.status === "signed" ? signer.updatedAt : signer.createdAt,
      status: signerStatusOf(signer.status),
      declineReason: signer?.declineReason,
      actions:
        signer.fileVersionId || canAct ? (
          <>
            {signer.fileVersionId && (
              <Button size="slim" variant="plain" onClick={() => handleDownloadFileVersion(fileEntity.id, signer.id)}>
                {t("download")}
              </Button>
            )}
            {canAct && (
              <>
                <Button size="slim" onClick={() => handleClickSign(fileEntity.id)}>
                  {t("sign")}
                </Button>
                <Button size="slim" onClick={() => handleDeclineSignRequest(fileEntity.id)}>
                  {t("decline")}
                </Button>
              </>
            )}
          </>
        ) : undefined,
    };
  });

  // The person who opened a document to sign it should not scroll past its data first,
  // so on a phone this card is drawn above the data (see the Layout below).
  const signatureCard =
    fileEntity.signType === "Click" ? (
      // if document generated by the user and then "file uploaded" user and manager can click to sign the file
      <Card roundedAbove="sm">
        <InlineStack align="center">{t("document_signature")}</InlineStack>
        <InlineStack align="space-between">
          <Button variant="plain" onClick={() => handlePdfView(fileEntity.id)} accessibilityLabel="Preview">
            {t("view")}
          </Button>
        </InlineStack>
        <BlockStack gap="400">
          {clickRows.length > 0 && <SignerList rows={clickRows} />}
          {isSigned && (
            <InlineStack align="center">
              <Badge tone="success">{t("signed")}</Badge>
            </InlineStack>
          )}
        </BlockStack>
      </Card>
    ) : fileEntity.fileId && isPdf ? (
      <Card roundedAbove="sm">
        <InlineStack align="center">{t("document_signature")}</InlineStack>
        <InlineStack align="end">
          <Button variant="plain" onClick={() => handlePdfView(fileEntity.id)} accessibilityLabel="Preview">
            {t("view")}
          </Button>
        </InlineStack>
        {fileEntity.signType === "Dokobit" && dokobitSigners.length > 0 && <SignerList rows={dokobitRows} />}
        {fileEntity.signType === "Signontab" && signontabSigners.length > 0 && <SignerList rows={signontabRows} />}
        <InlineStack align="center">{isSigned && <Badge tone="success">{t("signed")}</Badge>}</InlineStack>
      </Card>
    ) : null;

  return (
    <Page
      fullWidth
      title={fileEntity.name}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          {fileEntity.annulledAt && (
            <Badge tone="critical">{t("annulled_badge")}</Badge>
          )}
        </div>
      }
    >
      <Layout>
        {companyId && fileId && (
          <Layout.Section>
            <DocumentAnnulmentPanel
              companyId={companyId}
              fileId={fileId}
              fileName={fileEntity.name || fileEntity.title}
              annulledAt={fileEntity.annulledAt}
            />
          </Layout.Section>
        )}
        {isPhone && signatureCard && (
          <Layout.Section>
            {signatureCard}
          </Layout.Section>
        )}
        <Layout.Section>
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <InlineStack align="space-between">
                <Link
                  url={`${API_URL}/${companyId}/files/download/${fileEntity.id}`}
                  target="_blank"
                  removeUnderline
                >
                  {t("download")}
                </Link>

                <InlineStack gap="400" blockAlign="center">
                  {/* Deliberately ungated: a cancelled or completed document
                      must still expose its reminder history, and this is the
                      only page every document link in the app opens. */}
                  <button
                    type="button"
                    onClick={handleSigningHistoryClick}
                    className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 cursor-pointer text-sm text-sky-700 hover:underline max-md:min-h-11 [@media(pointer:coarse)]:min-h-11"
                  >
                    <History size={16} aria-hidden="true" />
                    <span>{t("signing_history")}</span>
                  </button>

                  {(isUserMaster || hasUserDocsPermission) && fileViewUrl && (
                    <Button
                      onClick={() => {
                        if (fileViewUrl) navigate(fileViewUrl);
                      }}
                    >
                      {t("show_more")}
                    </Button>
                  )}
                </InlineStack>
              </InlineStack>
              <TextContainer>
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_name")} : </strong>{" "}
                  {fileEntity?.name || fileEntity.title}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("customer")} : </strong> {customersNode}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("category")} : </strong> {categoriesNode}
                </Text>
                {fileEntity.assignedUser?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("responsible_user")} : </strong>{" "}
                    {fileEntity.assignedUser?.name}
                  </Text>
                )}
                {fileEntity.user?.name && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("the_file_was_uploaded_by")} : </strong>{" "}
                    {fileEntity.user?.name}
                  </Text>
                )}
                {!fileEntity.externalNumber ? (
                  <></>
                ) : (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("externalNumber")} : </strong>{" "}
                    {fileEntity.externalNumber}
                  </Text>
                )}
                {fileEntity.series && (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("series")} : </strong> {fileEntity.series.title}
                    {formatFileNumberWithSubNumber(
                      fileEntity.number,
                      fileEntity.subNumber
                    )}
                  </Text>
                )}
                {customFieldsContent}
                <Text as="p" variant="bodyMd">
                  <strong>{t("document_date")} : </strong>{" "}
                  {fileEntity.date ? (
                    <PrettyDate dateString={fileEntity.date} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("dateTo")} : </strong>{" "}
                  {fileEntity.dateTo ? (
                    <PrettyDate dateString={fileEntity.dateTo} />
                  ) : (
                    "N/A"
                  )}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("createdAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.createdAt}
                    showTime={true}
                  />
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>{t("updatedAt")} : </strong>{" "}
                  <PrettyDate
                    dateString={fileEntity.updatedAt}
                    showTime={true}
                  />
                </Text>
              </TextContainer>
            </Card>
          </BlockStack>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <InlineStack align="center">{t("review_by")}</InlineStack>
              {fileEntity.reviewByUsers?.length > 0 ? (
                <div className="content_break content_margin">
                  {fileEntity.reviewByUsers.map((entity, key) => (
                    <Text variant="bodyMd" as="p" key={key}>
                      {t("review_by")} : {entity?.user?.name}{" "}
                      {entity.reviewed ? (
                        <>
                          <PrettyDate
                            dateString={entity.updatedAt}
                            showTime={true}
                          />{" "}
                          <Badge tone="success">{t("reviewed")}</Badge>{" "}
                        </>
                      ) : (
                        <>
                          <PrettyDate
                            dateString={entity?.createdAt || ""}
                            showTime={true}
                          />{" "}
                          <Badge tone="attention">{t("pending")}</Badge>{" "}
                          {role?.user === entity?.user?.id && (
                            <Button
                              onClick={() =>
                                handleReviewClick({
                                  id: entity.id,
                                  fileId: entity.file,
                                  disabled: entity.reviewed,
                                })
                              }
                              accessibilityLabel="Preview"
                            >
                              {t("mark_as_reviewed")}
                            </Button>
                          )}{" "}
                        </>
                      )}
                    </Text>
                  ))}
                </div>
              ) : (
                <>{t("no_review_required")}</>
              )}
            </Card>

            {!isPhone && signatureCard}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default FileContainer;
